/** 素材列表按日期 / 状态分组（基于 safe DTO，不含 prompt/meta） */

export interface GroupableAsset {
  id: number;
  created_at: string;
  status: string;
}

export interface MaterialAssetGroup<T extends GroupableAsset = GroupableAsset> {
  key: string;
  label: string;
  assets: T[];
}

function startOfDay(d: Date): Date {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
}

function formatDateLabel(d: Date): string {
  const today = startOfDay(new Date());
  const target = startOfDay(d);
  const diff = Math.round((today.getTime() - target.getTime()) / 86400000);
  if (diff === 0) return '今天';
  if (diff === 1) return '昨天';
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

export function groupMaterialAssets<T extends GroupableAsset>(assets: T[]): MaterialAssetGroup<T>[] {
  const generating: T[] = [];
  const pending: T[] = [];
  const byDate = new Map<string, T[]>();

  for (const a of assets) {
    if (a.status === 'generating') {
      generating.push(a);
      continue;
    }
    if (a.status === 'pending_review') {
      pending.push(a);
      continue;
    }
    const label = formatDateLabel(new Date(a.created_at));
    const list = byDate.get(label) ?? [];
    list.push(a);
    byDate.set(label, list);
  }

  const groups: MaterialAssetGroup<T>[] = [];
  if (generating.length) groups.push({ key: 'generating', label: '生成中', assets: generating });
  if (pending.length) groups.push({ key: 'pending_review', label: '待审核', assets: pending });

  const dateLabels = [...byDate.keys()].sort((a, b) => {
    if (a === '今天') return -1;
    if (b === '今天') return 1;
    if (a === '昨天') return -1;
    if (b === '昨天') return 1;
    return b.localeCompare(a);
  });

  for (const label of dateLabels) {
    const list = byDate.get(label)!;
    const completed = list.filter((x) => x.status === 'ready' || x.status === 'failed');
    const key = label === '今天' || label === '昨天' ? label : `date-${label}`;
    groups.push({
      key,
      label: label === '今天' || label === '昨天' ? label : `已完成 · ${label}`,
      assets: (completed.length ? completed : list) as T[],
    });
  }

  return groups;
}
